import { queryOptions } from '@tanstack/react-query'

import Client from '@/api/client'
import { getConfig } from '@/app/config'
import { Document } from '@/features/documents'
import { downloadBlob } from '@/utils/download'
import { getItem } from '@/utils/storage'

import { CompanyDocument, DocumentTemplate } from '../domain/documents'

const base = getConfig().API_ENDPOINT ?? ''
const client = new Client(base)

type DocumentTemplatesSearchResult = DocumentTemplate[]

export const getDocumentTemplates = async (): Promise<DocumentTemplate[]> => {
  return await client.get<DocumentTemplatesSearchResult>('/api/documents/company-document-templates/list')
}

export const documentTemplatesQueryOptions = () =>
  queryOptions({
    queryKey: ['documentTemplates'],
    queryFn: getDocumentTemplates,
  })

export type DeleteDocumentProps = {
  customerId: string
  documentId: string
}

export const deleteDocument = async (props: DeleteDocumentProps): Promise<void> => {
  await client.delete(`/api/documents/documents/customers/${props.customerId}/${props.documentId}`)
}

export const getCustomerDocuments = async (customerId: string): Promise<Document[]> => {
  return await client.get<Document[]>(`/api/documents/documents/customer/${customerId}/list/all?entityType=customer`)
}

export const getEntityDocument = async (
  entityId: string,
  documentId: string,
  documentName: string
): Promise<{ blob: Blob; fileName: string }> => {
  const token = getItem('token')
  const response = await fetch(`${base}/api/documents/documents/customers/${entityId}/${documentId}/download`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  const blob = await response.blob()
  return { blob, fileName: documentName }
}

export const triggerDownloadForDocumentTemplate = async (
  documentId: string,
  language: string,
  fileName: string
): Promise<void> => {
  const token = getItem('token')
  // TODO: Replace with `client.getFile` once when non-JSON mime-types are supported
  const response = await fetch(`${base}/api/documents/company-document-templates/${documentId}?language=${language}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  const blob = await response.blob()
  downloadBlob(blob, fileName)
}

export type CreateDocumentProps = {
  customerId: string
  data: {
    file: File
    state: 'published'
    mimeType: string
    customerID: string
    source: 'customer'
    documentTypeCode: string
  }
  isNewRequiredActionsModels: boolean
}

export const createDocument = async (props: CreateDocumentProps): Promise<CompanyDocument> => {
  const { file, ...rest } = props.data

  if (props.isNewRequiredActionsModels) {
    return await client.postFile<CompanyDocument>(`/api/documents/documents/customer/${props.customerId}`, file, rest)
  }

  return await client.postFile<CompanyDocument>(`/api/documents/documents/customers/${props.customerId}`, file, {
    json: JSON.stringify(rest),
  })
}

export type UpdateDocumentProps = {
  customerId: string
  documentId: string
  data: {
    file: File
    state: 'published'
    mimeType: string
    customerID: string
    source: 'customer'
    documentTypeCode: string
  }
}

export const updateDocument = async (props: UpdateDocumentProps): Promise<CompanyDocument> => {
  const { file, ...rest } = props.data
  return await client.postFile<CompanyDocument>(
    `/api/documents/documents/customers/${props.customerId}/documents/${props.documentId}`,
    file,
    {
      json: JSON.stringify(rest),
    }
  )
}
